// src/views/kiddo-games/letters/components/FinalSummary.tsx
import React, {
  useEffect, useState, useCallback, useRef, memo,
} from 'react';
import { Button } from 'antd';
import { useTranslation } from 'react-i18next';
// Kopia repeat/play-circle-game.svg dla Easy — obwódka z kreski, żeby styl mógł ją pocienić jak na ekranie końcowym
import { ReactComponent as SoundIcon } from '../../../../assets/icons/games/kiddo/play-circle-game.svg';
import { ReactComponent as NextIcon } from '../../../../assets/icons/games/writing/arrow-right-rec.svg';
import { BASE_API_URL } from '../../../../services/ApiService';
import { notifyAudioError } from '../../../../utils/audioErrors';

interface FinalSummaryProps {
  word: string;
  image: string;
  onNext: () => void;
}

export const FinalSummary = memo(({
  word,
  image,
  onNext,
}: FinalSummaryProps) => {
  const { t } = useTranslation();
  const [isPlaying, setIsPlaying] = useState(false);
  const currentAudioRef = useRef<HTMLAudioElement | null>(null);
  const isPlayingRef = useRef(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null); // 🆕 Ref dla timeout

  const letters = word.split('');
  const letterKeys = letters.map((letter, idx) => `${word}-${idx}-${letter}`);

  // 🆕 Funkcja zatrzymująca odtwarzanie
  const stopPlayback = useCallback(() => {
    // Zatrzymaj audio
    if (currentAudioRef.current) {
      try {
        currentAudioRef.current.pause();
        currentAudioRef.current.currentTime = 0;
        currentAudioRef.current.src = ''; // Wyczyść src
      } catch (e) {
        // Ignoruj błędy
      }
      currentAudioRef.current = null;
    }

    // Wyczyść timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    // Reset stanów
    isPlayingRef.current = false;
    setIsPlaying(false);
  }, []);

  // Odtwórz sekwencję: każda litera + całe słowo
  const playSequence = useCallback(() => {
    if (isPlayingRef.current) return;

    isPlayingRef.current = true;
    setIsPlaying(true);

    const queue = [...letters, word];
    let index = 0;

    const playNext = () => {
      // 🔧 Guard - sprawdź czy nadal powinniśmy grać
      if (!isPlayingRef.current) return;

      if (index >= queue.length) {
        isPlayingRef.current = false;
        setIsPlaying(false);

        return;
      }

      const current = queue[index];
      const isLastItem = index === queue.length - 1;

      if (currentAudioRef.current) {
        try {
          currentAudioRef.current.pause();
          currentAudioRef.current.currentTime = 0;
        } catch (e) {
          // Ignoruj błędy
        }
        currentAudioRef.current = null;
      }

      const audio = new Audio(`${BASE_API_URL}/v1/tts?q=${encodeURIComponent(current)}`);
      currentAudioRef.current = audio;

      const handleEnded = () => {
        if (!isPlayingRef.current) return; // 🔧 Guard
        index += 1;
        const delay = isLastItem ? 0 : (index === letters.length ? 500 : 300);
        timeoutRef.current = setTimeout(playNext, delay); // 🔧 Zapisz timeout
      };

      const handleError = () => {
        if (!isPlayingRef.current) return; // 🔧 Guard
        console.error('Audio error');
        index += 1;
        timeoutRef.current = setTimeout(playNext, 300); // 🔧 Zapisz timeout
      };

      audio.addEventListener('ended', handleEnded);
      audio.addEventListener('error', handleError);

      const playPromise = audio.play();
      if (playPromise !== undefined) {
        playPromise.catch((err) => {
          if (err.name !== 'AbortError' && err.name !== 'NotAllowedError') {
            console.error('Play error:', err);
            notifyAudioError(err);
          }
          if (!isPlayingRef.current) return; // 🔧 Guard
          index += 1;
          timeoutRef.current = setTimeout(playNext, 300); // 🔧 Zapisz timeout
        });
      }
    };

    playNext();
  }, [letters, word]);

  // 🆕 Handler dla przycisku NEXT - zatrzymaj audio przed przejściem dalej
  const handleNext = useCallback(() => {
    stopPlayback();
    onNext();
  }, [stopPlayback, onNext]);

  // Auto-play tylko raz przy mount
  useEffect(() => {
    const autoPlayTimer = setTimeout(() => {
      if (!isPlayingRef.current) {
        playSequence();
      }
    }, 800);

    return () => {
      clearTimeout(autoPlayTimer);
      stopPlayback(); // 🔧 Użyj stopPlayback w cleanup
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // PUSTA! Tylko mount/unmount

  return (
    <div className="final-summary">
      {image && (
        <div className="summary-image">
          <img src={image} alt={word} />
        </div>
      )}

      <div className="summary-word">
        {letters.map((letter, index) => (
          <span key={letterKeys[index]} className="summary-letter">
            {letter.toUpperCase()}
          </span>
        ))}
      </div>

      <div className="summary-controls">
        <Button
          className="repeat-button"
          onClick={playSequence}
          disabled={isPlaying}
        >
          <SoundIcon className="btnLabel" />
          <br />
          {t('games:buttons.repeat')}
        </Button>

        <Button
          className="next-button"
          onClick={handleNext}
          type="primary"
        >
          <NextIcon className="btnLabel" />
          <br />
          {t('games:buttons.next')}
        </Button>
      </div>
    </div>
  );
});
